import 'date-fns';
import { COLOR_VARIANTS } from '@lgc/database/types/domains/UI';
export { COLOR_VARIANTS } from '@lgc/database/types/domains/UI';

// src/datetime/formatters.ts
function formatDuration(minutes) {
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  return `${hours} hour${hours === 1 ? "" : "s"} ${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}`;
}

// src/shared/hours.ts
var MINUTE_EPSILON = 1e-9;
function hoursLabel(hours) {
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}
function roundHours(minutes, decimals = 1) {
  const scale = 10 ** decimals;
  return Math.round(minutes * scale / 60) / scale;
}
var PLAIN_DECIMAL = /^-?(\d+\.?\d*|\.\d+)$/;
function decimalPlaces(text) {
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : text.slice(dot + 1).replace(/0+$/, "").length;
}
function roundDecimal(text, decimals) {
  return Number(`${Math.round(Number(`${text}e${decimals}`))}e-${decimals}`);
}
function parseHoursInput(raw, options = {}) {
  const { min = 0, max, allowEmpty = false, decimals = 2, excessDecimals = "refuse" } = options;
  const text = raw == null ? "" : String(raw).trim();
  if (text === "") {
    return allowEmpty ? { ok: true, empty: true } : { ok: false, reason: "empty", message: "Enter a number of hours" };
  }
  if (!PLAIN_DECIMAL.test(text)) return { ok: false, reason: "not_a_number", message: "Must be a number" };
  const typed = Number(text);
  let hours = typed;
  let adjusted = false;
  if (decimalPlaces(text) > decimals) {
    if (excessDecimals === "refuse") {
      return {
        ok: false,
        reason: "too_many_decimals",
        message: decimals === 0 ? "Must be a whole number of hours" : `Use up to ${decimals} decimal place${decimals === 1 ? "" : "s"}`
      };
    }
    hours = roundDecimal(text, decimals);
    adjusted = true;
  }
  if (hours < min) {
    return { ok: false, reason: "below_min", message: min === 0 ? "Cannot be negative" : `Must be at least ${hoursLabel(min)}` };
  }
  if (max !== void 0 && hours > max) {
    return { ok: false, reason: "above_max", message: `Must be ${hoursLabel(max)} or less` };
  }
  const exact = hours * 60;
  const minutes = Math.round(exact);
  return {
    ok: true,
    empty: false,
    minutes,
    hours,
    rounded: Math.abs(exact - minutes) > MINUTE_EPSILON,
    adjusted,
    label: formatDuration(minutes)
  };
}
function formatHoursInput(minutes) {
  return minutes != null ? String(roundHours(minutes, 2)) : "";
}
function formatHoursDisplay(minutes, options = {}) {
  const { decimals = 1, unit = "h", trimZeros = true } = options;
  const hours = roundHours(minutes, decimals);
  if (unit === " hours") return hoursLabel(hours);
  return `${trimZeros ? hours : hours.toFixed(decimals)}${unit}`;
}

// src/shared/formatters.ts
function minutesToHours(minutes) {
  return minutes / 60;
}
function hoursToMinutes(hours) {
  return Math.round(Number(hours) * 60);
}
function hoursToMinutesOrNull(hours) {
  if (hours == null || typeof hours === "string" && hours.trim() === "") return null;
  const minutes = hoursToMinutes(hours);
  return Number.isFinite(minutes) ? minutes : null;
}
function minutesToHoursStr(minutes) {
  return formatHoursInput(minutes);
}
function formatMinutesToHours(minutes) {
  if (minutes === 0) return "0m";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
function formatHours(hours, fallback = "0.0h") {
  if (hours == null) return fallback;
  return `${hours.toFixed(2).replace(/0$/, "")}h`;
}
function parseTimeStringToMinutes(timeStr) {
  const hourMatch = timeStr.match(/(\d+)h/);
  const minMatch = timeStr.match(/(\d+)m/);
  const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
  const minutes = minMatch ? parseInt(minMatch[1]) : 0;
  return hours * 60 + minutes;
}
function formatConstraint(minutes, label) {
  if (minutes === null) {
    return `${label}: Unlimited`;
  }
  return `${label}: ${formatMinutesToHours(minutes)}`;
}
function formatPrice(cents, currency = "USD") {
  const dollars = cents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(dollars);
}
function pluralize(word, count) {
  return count === 1 ? word : `${word}s`;
}

// src/shared/calculations.ts
function calculateUsagePercentage(used, limit) {
  if (limit === null || limit === 0) return 0;
  return Math.min(used / limit * 100, 100);
}
function calculateRemaining(used, limit) {
  if (limit === null) return null;
  return Math.max(0, limit - used);
}
function isLimitExceeded(used, limit) {
  if (limit === null) return false;
  return used > limit;
}
function calculateHourlyRate(totalCents, durationMinutes) {
  if (durationMinutes === 0) return 0;
  const hours = durationMinutes / 60;
  return Math.round(totalCents / hours);
}
function getEffectiveValue(userValue, defaultValue) {
  return userValue !== null && userValue !== void 0 ? userValue : defaultValue;
}

// src/shared/email.ts
var PLACEHOLDER_EMAIL_DOMAIN = "@buttersoftware.temp";
function isPlaceholderEmail(email) {
  return email.trim().toLowerCase().endsWith(PLACEHOLDER_EMAIL_DOMAIN);
}
function generatePlaceholderEmail(sourceId) {
  const safe = sourceId.toLowerCase().replace(/[^a-z0-9._-]/g, "-").replace(/^[._-]+|[._-]+$/g, "") || "unknown";
  return `noemail.${safe}${PLACEHOLDER_EMAIL_DOMAIN}`;
}
function realEmail(email) {
  const trimmed = (email ?? "").trim();
  return trimmed === "" || isPlaceholderEmail(trimmed) ? void 0 : trimmed;
}

// src/shared/name.ts
var PLACEHOLDER_NAME = "[unknown]";
function isPlaceholderName(name) {
  return name.trim().toLowerCase().includes(PLACEHOLDER_NAME);
}
function generatePlaceholderName() {
  return { firstName: PLACEHOLDER_NAME, lastName: PLACEHOLDER_NAME };
}
var PLACEHOLDER_NAME_TOKEN = /\[unknown\]/gi;
function stripPlaceholderName(name) {
  return (name ?? "").replace(PLACEHOLDER_NAME_TOKEN, " ").replace(/\s+/g, " ").trim();
}
function formatDisplayName(firstName, lastName, fallback = "") {
  return stripPlaceholderName(`${firstName ?? ""} ${lastName ?? ""}`) || fallback;
}
function isNamelessHalf(half) {
  const v = (half ?? "").trim();
  return v === "" || v.toLowerCase() === PLACEHOLDER_NAME;
}
function nameForStorage(firstName, lastName) {
  return {
    firstName: isNamelessHalf(firstName) ? PLACEHOLDER_NAME : (firstName ?? "").trim(),
    lastName: isNamelessHalf(lastName) ? PLACEHOLDER_NAME : (lastName ?? "").trim()
  };
}

// src/shared/accountSetup.ts
function needsAccountSetup(account) {
  const state = account.authStatus ?? account.status;
  return state === "pending" || state === "incomplete";
}
function getRandomColor() {
  return COLOR_VARIANTS[Math.floor(Math.random() * COLOR_VARIANTS.length)];
}

// src/shared/stripeError.ts
var STRIPE_ERROR_MESSAGES = {
  insufficient_funds: "Your card has insufficient funds. Please try a different payment method.",
  expired_card: "Your card has expired. Please use a different card.",
  // CVC / security code. Stripe emits both `incorrect_cvc` and `invalid_cvc`
  // (the latter is what a LIVE-mode confirm returns when the security code
  // fails validation) — both map to the same actionable copy.
  incorrect_cvc: "Your card's security code is incorrect. Please check it and try again.",
  invalid_cvc: "Your card's security code is incorrect. Please check it and try again.",
  cvv_failure: "Your card's security code is incorrect. Please check it and try again.",
  // Card number
  incorrect_number: "Your card number is incorrect. Please check it and try again.",
  invalid_number: "Your card number is incorrect. Please check it and try again.",
  // Expiry date
  invalid_expiry_month: "Your card's expiration date is invalid. Please check it and try again.",
  invalid_expiry_year: "Your card's expiration date is invalid. Please check it and try again.",
  // Billing postal code
  incorrect_zip: "Your billing postal code is incorrect. Please check it and try again.",
  invalid_card: "Your card details are invalid. Please check them and try again.",
  // Card / currency not accepted by the issuer or account
  card_not_supported: "This card type is not supported. Please use a different card.",
  currency_not_supported: "This card does not support this currency. Please use a different card.",
  processing_error: "There was a problem processing your card. Please try again.",
  issuer_not_available: "Your bank could not be reached. Please try again shortly.",
  try_again_later: "Your card was declined. Please try again shortly.",
  authentication_required: "Your bank needs to verify this card. Please try a different card or contact your bank.",
  // Security-related declines — neutral copy on purpose.
  stolen_card: "Your card was declined. Please contact your bank or use a different card.",
  lost_card: "Your card was declined. Please contact your bank or use a different card.",
  fraudulent: "Your card was declined. Please contact your bank or use a different card.",
  pickup_card: "Your card was declined. Please contact your bank or use a different card."
};
var DEFAULT_STRIPE_ERROR_MESSAGE = "Your card was declined. Please try a different payment method.";
function mapStripeCardError(error) {
  if (!error) return void 0;
  if (error.decline_code && STRIPE_ERROR_MESSAGES[error.decline_code]) {
    return STRIPE_ERROR_MESSAGES[error.decline_code];
  }
  if (error.code && STRIPE_ERROR_MESSAGES[error.code]) {
    return STRIPE_ERROR_MESSAGES[error.code];
  }
  return void 0;
}
function describeStripeConfirmError(result, fallback = "Payment method setup was not completed. Please try again.") {
  const branded = result?.method === "bank" ? void 0 : mapStripeCardError({ code: result?.code, decline_code: result?.declineCode });
  return branded ?? (result?.error ? withProviderErrorPrefix("stripe", result.error) : fallback);
}
var STRIPE_INFRASTRUCTURE_ERROR_TYPES = /* @__PURE__ */ new Set([
  "api_connection_error",
  "api_error",
  "authentication_error",
  "invalid_request_error",
  "rate_limit_error",
  "idempotency_error"
]);
function isStripeInfrastructureError(error) {
  if (!error?.type) return false;
  return STRIPE_INFRASTRUCTURE_ERROR_TYPES.has(error.type);
}
var PROVIDER_ERROR_LABELS = {
  stripe: "Stripe",
  square: "Square"
};
function withProviderErrorPrefix(provider, message) {
  const label = provider ? PROVIDER_ERROR_LABELS[provider] : null;
  if (!label) return message;
  const prefix = `${label} Error:`;
  return message.trimStart().toLowerCase().startsWith(prefix.toLowerCase()) ? message : `${prefix} ${message}`;
}

// src/shared/password.ts
var HAS_UPPERCASE = /[A-Z]/;
var HAS_LOWERCASE = /[a-z]/;
var HAS_NUMBER = /[0-9]/;
var HAS_SYMBOL = /[^A-Za-z0-9]/;
var USER_PASSWORD_MIN_LENGTH = 6;
var USER_PASSWORD_REQUIREMENT_TEXT = `At least ${USER_PASSWORD_MIN_LENGTH} characters, with an uppercase letter or a symbol.`;
function validateUserPassword(password) {
  const errors = [];
  if (!password) {
    return { isValid: false, errors: ["Password is required"] };
  }
  if (password.length < USER_PASSWORD_MIN_LENGTH) {
    errors.push(`Password must be at least ${USER_PASSWORD_MIN_LENGTH} characters.`);
  }
  if (!HAS_UPPERCASE.test(password) && !HAS_SYMBOL.test(password)) {
    errors.push("Password must include an uppercase letter or a symbol.");
  }
  return { isValid: errors.length === 0, errors };
}
function isStrongUserPassword(password) {
  return validateUserPassword(password).isValid;
}
var ADMIN_PASSWORD_MIN_LENGTH = 8;
var ADMIN_PASSWORD_REQUIREMENT_TEXT = `At least ${ADMIN_PASSWORD_MIN_LENGTH} characters, with uppercase and lowercase letters and a number.`;
function validateAdminPassword(password) {
  const errors = [];
  if (!password) {
    return { isValid: false, errors: ["Password is required"] };
  }
  if (password.length < ADMIN_PASSWORD_MIN_LENGTH) {
    errors.push(`Password must be at least ${ADMIN_PASSWORD_MIN_LENGTH} characters.`);
  }
  if (!HAS_UPPERCASE.test(password)) errors.push("Password must include an uppercase letter.");
  if (!HAS_LOWERCASE.test(password)) errors.push("Password must include a lowercase letter.");
  if (!HAS_NUMBER.test(password)) errors.push("Password must include a number.");
  return { isValid: errors.length === 0, errors };
}
function isStrongAdminPassword(password) {
  return validateAdminPassword(password).isValid;
}

// src/shared/appLinks.ts
var GENERIC_APP = {
  iosAppId: "6754621141",
  androidPackageName: "com.buttersoftware.generic",
  appBundleId: "com.buttersoftware.generic"
};
var APP_LINK_DOMAIN = "buttersoftware.com";
var GENERIC_APP_LINK_HOST = `generic.${APP_LINK_DOMAIN}`;
var APP_LINK_PATH_PREFIX = "/app";
var GENERIC_APP_LINK_IDS = {
  iosTeamId: "LJ24YQP559",
  androidCertFingerprints: []
};
function appHostFor(tenant) {
  return tenant.appStatus === "live" ? `${tenant.handle.toLowerCase()}.${APP_LINK_DOMAIN}` : GENERIC_APP_LINK_HOST;
}
function buildAppLink(tenant, clubId, portalPath, extra = {}) {
  const query = new URLSearchParams({ "tenant-handle": tenant.handle, "club-id": String(clubId), ...extra });
  const path = portalPath.startsWith("/") ? portalPath : `/${portalPath}`;
  return `https://${appHostFor(tenant)}${APP_LINK_PATH_PREFIX}${path}?${query.toString()}`;
}
function clubInstallReferrer(handle, clubId) {
  return new URLSearchParams({ "tenant-handle": handle, "club-id": String(clubId) }).toString();
}
function generateAppStoreLink(iosAppId) {
  return `https://apps.apple.com/app/id${encodeURIComponent(iosAppId)}`;
}
function generatePlayStoreLink(androidPackageName, referrer) {
  const base = `https://play.google.com/store/apps/details?id=${encodeURIComponent(androidPackageName)}`;
  return referrer ? `${base}&referrer=${encodeURIComponent(referrer)}` : base;
}
function generateAppStoreLinks(tenant, options = {}) {
  const isIOSGeneric = !tenant?.iosAppId;
  const isAndroidGeneric = !tenant?.androidPackageName;
  const iosAppId = tenant?.iosAppId || GENERIC_APP.iosAppId;
  const androidPackageName = tenant?.androidPackageName || GENERIC_APP.androidPackageName;
  return {
    ios: generateAppStoreLink(iosAppId),
    android: generatePlayStoreLink(androidPackageName, isAndroidGeneric ? options.referrer : void 0),
    // Generic only when NEITHER platform has a dedicated app.
    isGenericApp: isIOSGeneric && isAndroidGeneric,
    isIOSGeneric,
    isAndroidGeneric
  };
}
function isRealIosAppId(iosAppId) {
  return Boolean(iosAppId && iosAppId.trim() !== "" && iosAppId !== GENERIC_APP.iosAppId);
}
function isRealAndroidPackageName(packageName) {
  return Boolean(packageName && packageName.trim() !== "" && packageName !== GENERIC_APP.androidPackageName);
}
function publishedStoreIds(tenant) {
  return {
    iosAppId: isRealIosAppId(tenant.iosAppId) && !tenant.iosLiveOn ? null : tenant.iosAppId,
    androidPackageName: isRealAndroidPackageName(tenant.androidPackageName) && !tenant.androidLiveOn ? null : tenant.androidPackageName
  };
}

// src/shared/acceptanceStatus.ts
function resolveAcceptanceStatus({
  acceptedVersion,
  activeVersion
}) {
  if (activeVersion == null) return "no-terms";
  if (acceptedVersion == null) return "no-record";
  return acceptedVersion >= activeVersion ? "current" : "outdated";
}
function needsAcceptance(status) {
  return status === "no-record" || status === "outdated";
}

// src/shared/richText.ts
var RICH_TEXT_TAGS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "ul",
  "ol",
  "li",
  "a",
  "blockquote",
  "code",
  "pre",
  "hr"
];
var RICH_TEXT_ATTRIBUTES = {
  a: ["href", "target", "rel"],
  ol: ["start", "type"],
  "*": ["class", "style"]
};
var RICH_TEXT_SCHEMES = ["http", "https", "mailto"];
var RICH_TEXT_STYLES = {
  "*": { "text-align": [/^(left|right|center|justify)$/] }
};
var RICH_TEXT_CLASSES = {
  "*": [/^language-/],
  a: ["text-blue-600", "underline"]
};
var RICH_TEXT_LINK_REL = "noopener noreferrer nofollow";
var RICH_TEXT_ATTRIBUTES_FLAT = Array.from(
  new Set(Object.values(RICH_TEXT_ATTRIBUTES).flat())
);

// src/shared/csv.ts
var FORMULA_START = /^[=+\-@\t\r]/;
function csvCell(value) {
  if (value === null || value === void 0) return "";
  const text = typeof value === "string" && FORMULA_START.test(value) ? `'${value}` : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
function toCsv(header, rows) {
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");
}

export { ADMIN_PASSWORD_MIN_LENGTH, ADMIN_PASSWORD_REQUIREMENT_TEXT, APP_LINK_DOMAIN, APP_LINK_PATH_PREFIX, DEFAULT_STRIPE_ERROR_MESSAGE, GENERIC_APP, GENERIC_APP_LINK_HOST, GENERIC_APP_LINK_IDS, PLACEHOLDER_EMAIL_DOMAIN, PLACEHOLDER_NAME, RICH_TEXT_ATTRIBUTES, RICH_TEXT_ATTRIBUTES_FLAT, RICH_TEXT_CLASSES, RICH_TEXT_LINK_REL, RICH_TEXT_SCHEMES, RICH_TEXT_STYLES, RICH_TEXT_TAGS, STRIPE_ERROR_MESSAGES, USER_PASSWORD_MIN_LENGTH, USER_PASSWORD_REQUIREMENT_TEXT, appHostFor, buildAppLink, calculateHourlyRate, calculateRemaining, calculateUsagePercentage, clubInstallReferrer, csvCell, describeStripeConfirmError, formatConstraint, formatDisplayName, formatHours, formatHoursDisplay, formatHoursInput, formatMinutesToHours, formatPrice, generateAppStoreLink, generateAppStoreLinks, generatePlaceholderEmail, generatePlaceholderName, generatePlayStoreLink, getEffectiveValue, getRandomColor, hoursToMinutes, hoursToMinutesOrNull, isLimitExceeded, isPlaceholderEmail, isPlaceholderName, isRealAndroidPackageName, isRealIosAppId, isStripeInfrastructureError, isStrongAdminPassword, isStrongUserPassword, mapStripeCardError, minutesToHours, minutesToHoursStr, nameForStorage, needsAcceptance, needsAccountSetup, parseHoursInput, parseTimeStringToMinutes, pluralize, publishedStoreIds, realEmail, resolveAcceptanceStatus, roundHours, stripPlaceholderName, toCsv, validateAdminPassword, validateUserPassword, withProviderErrorPrefix };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map